import { useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { authSessionKey, useIdleSignOut } from '@/hooks/useIdleSignOut';
import { IDLE_ACTIVITY_STORAGE_KEY } from '@/constants/sessionIdle';

/** How long the auth client gets to end the session before this browser forgets it locally. */
export const IDLE_SIGN_OUT_ATTEMPT_MS = 8_000;

type AuthClientInternals = { storageKey?: string };

/**
 * Last resort when the auth client cannot end the session: this browser
 * forgets it (the stored session and PKCE verifier) and reloads at /auth,
 * which boots signed out. Other tabs reach the same verdict from the shared
 * inactivity record and do the same.
 *
 * The inactivity record is deliberately KEPT. The server-side session was not
 * revoked, and another still-open tab's auth client may write the same
 * session back to storage on its next token refresh; with the record kept,
 * that session (same session_id) meets the same EXPIRED verdict the moment
 * any tab loads it, instead of a fresh baseline. A new sign-in has a new
 * session_id and starts fresh.
 */
export function forgetSessionLocally(storage: Storage = window.localStorage, go: (url: string) => void = (u) => window.location.assign(u)) {
  const key = (supabase.auth as unknown as AuthClientInternals).storageKey;
  try {
    if (key) {
      storage.removeItem(key);
      storage.removeItem(`${key}-code-verifier`);
    }
  } catch { /* storage unavailable: the reload still drops in-memory state */ }
  go('/auth');
}

/**
 * Signs an inactive browser out (policy: src/constants/sessionIdle.ts).
 *
 * First the canonical path: `supabase.auth.signOut({ scope: 'local' })` ends
 * THIS browser's session only (an idle tablet must not sign the same person
 * out of their phone) and broadcasts SIGNED_OUT to every other tab, whose
 * AuthContext listener performs the canonical cleanup (identity storage keys,
 * query cache, feature flags) and ProtectedRoute returns it to /auth.
 *
 * That call waits, without a timeout, on the auth client's lock and needs a
 * live session first — after hours asleep the access token has expired and
 * the refresh can stall, so the sign-out never finishes (reproduced in a real
 * browser). An idle sign-out must not depend on it: if it errors or has not
 * finished within IDLE_SIGN_OUT_ATTEMPT_MS, this browser forgets the session
 * locally and reloads at /auth.
 */
export function IdleSignOutGuard() {
  const { user, session } = useAuth();
  // A string that stays the same across token refreshes (session_id claim)
  // and changes with every new sign-in.
  const sessionKey = user ? authSessionKey(session?.access_token, user.id) : null;
  const warning = useRef<{ dismiss: () => void } | null>(null);

  const endSession = useCallback(async () => {
    type Outcome = 'ok' | 'error' | 'timeout';
    const outcome = await Promise.race<Outcome>([
      supabase.auth.signOut({ scope: 'local' }).then(({ error }): Outcome => (error ? 'error' : 'ok'), (): Outcome => 'error'),
      new Promise<Outcome>((resolve) => setTimeout(() => resolve('timeout'), IDLE_SIGN_OUT_ATTEMPT_MS)),
    ]);
    if (outcome === 'ok') {
      try { localStorage.removeItem(IDLE_ACTIVITY_STORAGE_KEY); } catch { /* storage unavailable */ }
      return;
    }
    forgetSessionLocally();
  }, []);

  const warn = useMemo(() => ({
    show: (msLeft: number) => {
      warning.current?.dismiss();
      const minutes = Math.max(1, Math.round(msLeft / 60_000));
      warning.current = toast({
        title: 'Still there?',
        description: `Your session will expire due to inactivity in about ${minutes} minute${minutes === 1 ? '' : 's'}. Tap anywhere to stay signed in.`,
        duration: msLeft,
        skipPersist: true,
      } as Parameters<typeof toast>[0]);
    },
    dismiss: () => { warning.current?.dismiss(); warning.current = null; },
  }), []);

  useIdleSignOut({ sessionKey, signOut: endSession, warn });
  return null;
}
